import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Upload,
  FileText,
  Table,
  ArrowLeft,
  X,
  Loader2,
  Image,
  AlertCircle,
  Plus,
  Trash2,
  Clipboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ThemeService from '../services/ThemeService';

const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_PDF_FILE_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_FILES = 500;

const NewProjectPage = () => {
  const [dragActive, setDragActive] = useState(false);
  const [pasteActive, setPasteActive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [projectName, setProjectName] = useState('');
  const [projectType, setProjectType] = useState('pdf'); // 'pdf' or 'snippet'
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPasteHint, setShowPasteHint] = useState(false);
  const pasteTimeout = useRef(null);
  const mainContentRef = useRef(null);
  const navigate = useNavigate();

  // Initialize theme when component mounts
  useEffect(() => {
    // This ensures dark mode is applied if it's the current setting
    ThemeService.initialize();
  }, []);

  // Reset project name when project type changes
  useEffect(() => {
    if (selectedFiles.length > 0) {
      const file = selectedFiles[0];
      const extension = projectType === 'pdf' ? '.pdf' : 
        file.type.includes('jpeg') ? '.jpg' : '.png';
      setProjectName(file.name.replace(extension, '').replace(/[^a-zA-Z0-9]/g, ' '));
    } else {
      setProjectName('');
    }
  }, [projectType, selectedFiles]);

  // Add paste event listener
  useEffect(() => {
    const handlePaste = (e) => {
      // Only handle paste if in snippet mode
      if (projectType !== 'snippet') return;
      
      const clipboardItems = e.clipboardData.items;
      const imageItems = Array.from(clipboardItems).filter(item => 
        item.type.indexOf('image') !== -1
      );
      
      if (imageItems.length === 0) return;
      
      e.preventDefault();
      setPasteActive(true);
      
      // Show visual feedback
      setShowPasteHint(true);
      if (pasteTimeout.current) clearTimeout(pasteTimeout.current);
      pasteTimeout.current = setTimeout(() => {
        setShowPasteHint(false);
      }, 3000);
      
      // Process the pasted image
      const imageItem = imageItems[0]; // Take the first image if multiple
      const blob = imageItem.getAsFile();
      
      if (!blob) {
        setPasteActive(false);
        return;
      }
      
      // Create a File object from the blob
      const now = new Date();
      const fileName = `Pasted Image ${now.toLocaleString().replace(/[\/,:]/g, '-')}.png`;
      const file = new File([blob], fileName, { type: blob.type });
      
      // Validate and add the file
      const error = validateFile(file);
      if (error) {
        setError(error);
        setPasteActive(false);
        return;
      }
      
      // Check if adding this file would exceed the max files limit
      if (selectedFiles.length >= MAX_FILES) {
        setError(`You can upload a maximum of ${MAX_FILES} images for a snippet project`);
        setPasteActive(false);
        return;
      }
      
      // Add the file to selectedFiles
      setSelectedFiles(prev => [...prev, file]);
      setPasteActive(false);
    };
    
    // Add event listener to the document
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
      if (pasteTimeout.current) clearTimeout(pasteTimeout.current);
    };
  }, [projectType, selectedFiles]);

    const validateFile = (file) => {
    setError('');
      
    if (!file) {
      return 'Please select a file';
    }

    if (projectType === 'pdf' && file.type !== 'application/pdf') {
      return 'Please upload a PDF file for PDF projects';
    }

    if (projectType === 'snippet' && 
        !file.type.startsWith('image/') && 
        file.type !== 'image/tiff') {
      return 'Please upload image files (JPEG, PNG, or TIF) for Snippet projects';
    }

    if (projectType === 'snippet' &&  file.size > MAX_IMAGE_FILE_SIZE) {
      return `File size should be less than ${MAX_IMAGE_FILE_SIZE / (1024 * 1024)}MB`;
    }

    if (projectType === 'pdf' &&  file.size > MAX_PDF_FILE_SIZE) {
      return `File size should be less than ${MAX_PDF_FILE_SIZE / (1024 * 1024)}MB`;
    }



    return null; // No error
  };

  const handleFileSelect = useCallback((files) => {
    const fileArray = Array.from(files);
    
    // Check project type consistency
    const isPdfProject = fileArray.some(file => file.type === 'application/pdf');
    const isImageProject = fileArray.some(file => file.type.startsWith('image/'));
    
    if (isPdfProject && isImageProject) {
      setError('Cannot mix PDF and image files in the same project');
      return;
    }

    // Set project type based on first file
    const newProjectType = fileArray[0]?.type === 'application/pdf' ? 'pdf' : 'snippet';
    setProjectType(newProjectType);

    // Validate all files
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setError(error);
        return;
      }
    }

    // PDF projects can only have one file
    if (newProjectType === 'pdf' && fileArray.length > 1) {
      setError('PDF projects can only have one file');
      return;
    }

    // Check max files for snippet projects
    if (newProjectType === 'snippet' && fileArray.length > MAX_FILES) {
      setError(`You can upload a maximum of ${MAX_FILES} images for a snippet project`);
      return;
    }

    setSelectedFiles(fileArray);
  }, [projectType]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    handleFileSelect(files);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      
      if (newFiles.length === 0) {
        setProjectName('');
      }
      
      return newFiles;
    });
  };

  const handleUpload = async () => {
    if (!projectName.trim()) {
      setError('Please enter a project name');
      return;
    }

    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setProcessing(true);
    setError('');

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user) {
        throw new Error('User not found');
      }

      // Create FormData
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('file', file);
      });
      
      formData.append('projectName', projectName);
      formData.append('userId', user.userId);
      formData.append('projectType', projectType);

      // Upload files
      const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/upload?projectType=${projectType}`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const uploadData = await uploadResponse.json();
      console.log('Upload response:', uploadData);
      
      // For PDF projects, handle OCR processing
      if (projectType === 'pdf') {
        await processFile(uploadData.files[0].id);
      } else {
        // For snippet projects, process all files in parallel
        await Promise.all(uploadData.files.map(file => processFile(file.id)));
      }

      // Navigate to project workspace
      navigate(`/project/${uploadData.project.id}`);
    } catch (err) {
      setError(err.message || 'Failed to upload file. Please try again.');
    } finally {
      setProcessing(false);
      setUploadProgress(0);
    }
  };

  const processFile = async (fileId) => {
    try {
      const processResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/files/${fileId}/process`,
        { method: 'POST' }
      );

      if (!processResponse.ok) {
        throw new Error('Failed to process file');
      }

      return processResponse.json();
    } catch (error) {
      console.error('Processing error:', error);
      throw error;
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const getImageThumbnail = (file) => {
    // For TIF files, return a placeholder instead of trying to create a preview URL
    if (file.type === 'image/tiff') {
      return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWltYWdlIj48cmVjdCB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHg9IjMiIHk9IjMiIHJ4PSIyIiByeT0iMiIvPjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ii8+PHBvbHlsaW5lIHBvaW50cz0iMjEgMTUgMTYgMTAgNSAyMSIvPjwvc3ZnPg==';
    }
    return URL.createObjectURL(file);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 relative overflow-hidden transition-colors">
      {/* Background gradients - adjusted for dark mode */}
      <div 
        className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-pink-500/5 via-blue-500/5 to-purple-500/5 dark:from-pink-500/10 dark:via-blue-500/10 dark:to-purple-500/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl"
        aria-hidden="true"
      />
      <div 
        className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tl from-pink-500/5 via-blue-500/5 to-purple-500/5 dark:from-pink-500/10 dark:via-blue-500/10 dark:to-purple-500/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl"
        aria-hidden="true"
      />

      {/* Navigation bar */}
      <nav className="relative border-b border-gray-100 dark:border-gray-800 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={handleBack}
              className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
            <div className="text-2xl font-bold bg-gradient-to-r from-pink-500 via-blue-500 to-purple-500 bg-clip-text text-transparent">
              DigiTables
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main 
        className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16" 
        ref={mainContentRef}
        tabIndex="0" // Makes the element focusable
      >
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">Start a New Project</h1>
          <p className="text-gray-600 dark:text-gray-400">Choose what you want to start with</p>
        </div>

        {/* Project Type Selection */}
        <div className="mb-8">
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => {
                setProjectType('pdf');
                setSelectedFiles([]);
              }}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                projectType === 'pdf' 
                  ? 'bg-blue-50 text-blue-600 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <FileText size={20} />
              PDF Project
            </button>
            <button
              onClick={() => {
                setProjectType('snippet');
                setSelectedFiles([]);
                // Focus the main container to enable paste functionality
                if (mainContentRef.current) {
                  mainContentRef.current.focus();
                }
                // Show paste hint when snippet is selected
                setShowPasteHint(true);
                if (pasteTimeout.current) clearTimeout(pasteTimeout.current);
                pasteTimeout.current = setTimeout(() => {
                  setShowPasteHint(false);
                }, 5000);
              }}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-colors ${
                projectType === 'snippet' 
                  ? 'bg-green-50 text-green-600 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800' 
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Image size={20} />
              Snippet Project
            </button>
          </div>
        </div>

        {/* Paste Hint */}
        {projectType === 'snippet' && showPasteHint && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2 text-blue-600 dark:text-blue-400 animate-pulse">
            <Clipboard className="h-5 w-5" />
            <span>Pro tip: You can paste images directly using <kbd className="px-2 py-1 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-sm">Ctrl+V</kbd></span>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Upload Options Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* PDF Upload Option */}
          {projectType === 'pdf' && (
            <div className="relative group col-span-2">
              <input
                type="file"
                accept=".pdf"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                onChange={handleFileInput}
                disabled={processing}
              />
              <div className="h-full bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:border-blue-500/20 dark:hover:border-blue-500/40 hover:bg-blue-50/5 dark:hover:bg-blue-500/5 transition-all duration-300 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-blue-50 dark:bg-blue-500/20 rounded-full flex items-center justify-center mb-4">
                  <FileText size={24} className="text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">PDF with Tables</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Upload a PDF file and we'll automatically detect and extract all tables</p>
                <span className="text-xs text-gray-400 dark:text-gray-500">Drag & drop or click to upload</span>
              </div>
            </div>
          )}

          {/* Image Upload Option */}
          {projectType === 'snippet' && (
            <div className="relative group col-span-2">
              <input
                type="file"
                accept="image/*"
                multiple
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                onChange={handleFileInput}
                disabled={processing}
              />
              <div className="h-full bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 hover:border-green-500/20 dark:hover:border-green-500/40 hover:bg-green-50/5 dark:hover:bg-green-500/5 transition-all duration-300 flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-green-50 dark:bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                  <Image size={24} className="text-green-500 dark:text-green-400" />
                </div>
                <h3 className="font-semibold mb-2 text-gray-900 dark:text-white">Table Snippets</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Upload images containing tables and we'll convert them to editable data
                  <br />
                  <span className="text-xs text-gray-400 dark:text-gray-500 mt-1 block">
                    You can select multiple images (max {MAX_FILES})
                  </span>
                </p>
                <div className="flex flex-col items-center space-y-2">
                  <span className="text-xs text-gray-400 dark:text-gray-500">Drag & drop or click to upload</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center">
                    <Clipboard size={12} className="mr-1" /> or paste with Ctrl+V
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Selected Files Info with Project Name Input */}
        {selectedFiles.length > 0 && (
          <div className="mt-8 space-y-4">
            {/* Project Name Input */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label htmlFor="projectName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Project Name
              </label>
              <input
                type="text"
                id="projectName"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                placeholder="Enter project name"
              />
            </div>

            {/* File List */}
            <div className="space-y-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 flex items-center justify-between border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    {projectType === 'snippet' && (
                      <div className="w-12 h-12 rounded-md bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <img 
                          src={getImageThumbnail(file)} 
                          alt="Preview" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    {projectType === 'pdf' && (
                      <FileText size={20} className="text-blue-500 dark:text-blue-400" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{file.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    disabled={processing}
                  >
                    <Trash2 size={16} className="text-gray-400 dark:text-gray-500" />
                  </button>
                </div>
              ))}

              {/* Add more images button for snippet projects */}
              {projectType === 'snippet' && selectedFiles.length > 0 && selectedFiles.length < MAX_FILES && (
                <button
                  onClick={() => document.querySelector('input[type="file"]').click()}
                  className="w-full py-2 px-4 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  disabled={processing}
                >
                  <Plus size={16} />
                  Add more images
                </button>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex justify-end mt-4">
              {processing ? (
                <div className="flex items-center gap-2 text-blue-500 dark:text-blue-400 px-4 py-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Processing... {uploadProgress}%</span>
                </div>
              ) : (
                <button
                  onClick={handleUpload}
                  className="bg-black dark:bg-gray-800 text-white px-6 py-2.5 rounded-lg hover:bg-gray-900 dark:hover:bg-gray-700 transition-colors flex items-center gap-2 border border-transparent dark:border-gray-700"
                  disabled={!projectName.trim() || selectedFiles.length === 0}
                >
                  <Upload size={16} />
                  Upload and Process
                </button>
              )}
            </div>
          </div>
        )}

        {/* Drop Zone Overlay */}
        {dragActive && (
          <div 
            className="absolute inset-0 bg-blue-500/10 dark:bg-blue-500/20 backdrop-blur-sm flex items-center justify-center"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg flex flex-col items-center border border-gray-200 dark:border-gray-700">
              <Upload size={32} className="text-blue-500 dark:text-blue-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 dark:text-white">Drop your file{projectType === 'snippet' ? 's' : ''} here</p>
            </div>
          </div>
        )}

        {/* Paste Overlay */}
        {pasteActive && (
          <div className="absolute inset-0 bg-green-500/10 dark:bg-green-500/20 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-lg flex flex-col items-center border border-gray-200 dark:border-gray-700">
              <Clipboard size={32} className="text-green-500 dark:text-green-400 mb-4" />
              <p className="text-lg font-medium text-gray-900 dark:text-white">Processing pasted image...</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default NewProjectPage;